import { useState, useCallback, useEffect, useRef, isValidElement, type ReactNode } from 'react'
import { useReqdeskContext } from './ReqdeskProvider'
import { en } from '../i18n/en'
import { ar } from '../i18n/ar'
import { themeToStyle } from '../theme'
import { saveWidgetConfig, loadWidgetConfig, loadWidgetEmail, clearWidgetEmail } from '../storage'
import { login, logout, isAuthConfigured } from '../auth/widget-auth'
import { ShadowRoot } from './shadow-root'
import { SubmitTicketView } from './views/SubmitTicketView'
import { MyTicketsView } from './views/MyTicketsView'
import { TicketDetailView } from './views/TicketDetailView'
import { TrackTicketView } from './views/TrackTicketView'
import { MenuList, type BuiltInMenuItem } from './views/MenuList'
import { useRegistry, useRegistrySnapshot } from './registry-context'
import { NotificationStack } from './notifications'
import { normalizePosition, positionSide, type WidgetPosition, type AnyWidgetPosition } from '../position'
import { resolveFabIconPath } from '../ui/fab-icons'
import type { TicketResult, WidgetError } from './types'

const translations: Record<string, Record<string, string>> = { en, ar }

const CORNER_LABEL_KEY: Record<WidgetPosition, string> = {
  'top-start': 'prefs.topStart',
  'top-end': 'prefs.topEnd',
  'bottom-start': 'prefs.bottomStart',
  'bottom-end': 'prefs.bottomEnd',
}

const CORNERS: readonly WidgetPosition[] = ['top-start', 'top-end', 'bottom-start', 'bottom-end']

type WidgetView = 'home' | 'select-project' | 'new-ticket' | 'my-tickets' | 'ticket-detail' | 'track' | 'kb' | 'preferences'

interface FloatingWidgetProps {
  position?: AnyWidgetPosition
  contained?: boolean
  /** Width of the widget when expanded. Accepts any CSS length (e.g. "60vw", "800px", "70%"). Defaults to 60vw. */
  expandedWidth?: string
  /** When true, skip rendering the floating action button. Host drives opens via ReqdeskTrigger / mountTrigger / useReqdesk. */
  hideFab?: boolean
  onTicketCreated?: (ticket: TicketResult) => void
  onError?: (error: WidgetError) => void
}

const ICONS = {
  close: 'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z',
  newTicket: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z',
  tickets: 'M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 15.01l1.41 1.41L11 14.84V19h2v-4.16l1.59 1.59L16 15.01 12.01 11z',
  track: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
  kb: 'M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1zm0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5z',
  back: 'M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z',
  backRtl: 'M4 11h12.17l-5.59-5.59L12 4l8 8-8 8-1.41-1.41L16.17 13H4v-2z',
  settings: 'M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.49.49 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.49.49 0 00-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1115.6 12 3.6 3.6 0 0112 15.6z',
  switchProject: 'M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z',
  expand: 'M5 5h5V3H3v7h2V5zm5 14H5v-5H3v7h7v-2zm4-16v2h5v5h2V3h-7zm5 16h-5v2h7v-7h-2v5z',
  collapse: 'M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z',
  logout: 'M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z',
}

function SvgIcon({ path, size = 20 }: { path: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: 'block', flexShrink: 0 }}>
      <path d={path} />
    </svg>
  )
}

/**
 * Resolves the host-supplied `fabIcon` value. A `ReactNode` renders as-is so hosts can drop in
 * any JSX (SVG, icon-font <i>, <img>). A string is treated as a preset key or raw SVG path and
 * routed through the shared resolver so vanilla + React bundles stay visually identical.
 */
function renderFabIcon(fabIcon: 'chat' | 'help' | string | ReactNode | undefined, size: number): ReactNode {
  if (fabIcon != null && typeof fabIcon !== 'string') {
    // Object / element / number / boolean — anything a React child can be.
    if (isValidElement(fabIcon) || Array.isArray(fabIcon) || typeof fabIcon === 'number') return fabIcon as ReactNode
    // Unknown type — fall through to the default.
  }
  return <SvgIcon path={resolveFabIconPath(typeof fabIcon === 'string' ? fabIcon : undefined)} size={size} />
}

function buildPanelClassName(args: {
  mode: 'popover' | 'side-sheet' | 'bottom-sheet'
  side: 'start' | 'end'
  posClass: string
  contained: boolean
  isOpen: boolean
  isExpanded: boolean
  isRepositioning: boolean
}): string {
  const parts: string[] = ['rqd-panel']
  if (args.mode === 'popover') {
    parts.push(args.posClass)
  } else if (args.mode === 'side-sheet') {
    parts.push('rqd-mode-side-sheet', `rqd-side-${args.side}`)
  } else {
    parts.push('rqd-mode-bottom-sheet')
  }
  if (args.contained) parts.push('rqd-contained')
  if (!args.isOpen) parts.push('rqd-hidden')
  if (args.isExpanded && args.mode === 'popover') parts.push('rqd-expanded')
  if (args.isRepositioning) parts.push('rqd-repositioning')
  return parts.join(' ')
}

function buildPanelStyle(
  baseVars: Record<string, string | number>,
  display: { mode: string; width?: string; height?: string },
): React.CSSProperties {
  const style: Record<string, string | number> = { ...baseVars }
  if (display.mode === 'side-sheet' && display.width) {
    style['--rqd-sheet-width'] = display.width
  }
  if (display.mode === 'bottom-sheet' && display.height) {
    style['--rqd-sheet-height'] = display.height
  }
  return style as React.CSSProperties
}

export function FloatingWidget({
  position: propPosition = 'bottom-end',
  contained = false,
  expandedWidth,
  hideFab: hideFabProp,
  onTicketCreated,
  onError,
}: FloatingWidgetProps) {
  const ctx = useReqdeskContext()
  const registry = useRegistry()
  // Fall back to <ReqdeskProvider hideFab> so hosts can opt out of the FAB once at the
  // provider level instead of repeating the prop on every widget mount.
  const hideFab = hideFabProp ?? ctx.hideFab ?? false
  const currentDisplayMode = useRegistrySnapshot(s => s.currentDisplayMode)
  const [isOpen, setIsOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [view, setView] = useState<WidgetView>('home')
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null)
  const [emailCleared, setEmailCleared] = useState(false)

  const savedPrefs = loadWidgetConfig(ctx.apiKey)
  const [userLang, setUserLang] = useState<string | null>(savedPrefs?.language ?? null)
  const [userThemeMode, setUserThemeMode] = useState<string | null>(savedPrefs?.theme?.mode ?? null)
  const [userColor, setUserColor] = useState<string | null>(savedPrefs?.theme?.primaryColor ?? null)
  const [position, setPosition] = useState<WidgetPosition>(() =>
    normalizePosition(savedPrefs?.position ?? propPosition),
  )
  const [isRepositioning, setIsRepositioning] = useState(false)
  const repositionTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Escape collapses from expanded mode (without closing the widget)
  useEffect(() => {
    if (!isExpanded) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsExpanded(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isExpanded])

  // Escape closes the widget when open in any sheet mode (FR-029a). Popover mode relies on the
  // header close button.
  useEffect(() => {
    if (!isOpen) return
    if (currentDisplayMode.mode === 'popover') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
        registry.close()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, currentDisplayMode.mode, registry])

  // Mirror registry's isOpen into local state so programmatic open/close (from useReqdesk or
  // mountTrigger) reflects in the React tree immediately.
  const registryIsOpen = useRegistrySnapshot(s => s.isOpen)
  useEffect(() => {
    if (registryIsOpen && !isOpen) setIsOpen(true)
    if (!registryIsOpen && isOpen) setIsOpen(false)
  }, [registryIsOpen, isOpen])

  useEffect(() => () => {
    if (repositionTimer.current) clearTimeout(repositionTimer.current)
  }, [])

  const activeLang = userLang ?? ctx.language ?? 'en'
  const activeThemeMode = (userThemeMode ?? ctx.theme?.mode ?? 'auto') as 'light' | 'dark' | 'auto'
  const activeColor = userColor || ctx.theme?.primaryColor || '#0F5E56'
  const activeTheme = { ...ctx.theme, mode: activeThemeMode, primaryColor: activeColor }

  const t = useCallback((key: string): string => {
    if (ctx.translations?.[key]) return ctx.translations[key]
    const lang = translations[activeLang] ?? translations.en
    return lang[key] ?? key
  }, [activeLang, ctx.translations])

  const isRtl = activeLang === 'ar'
  const cssVars = {
    ...themeToStyle(activeTheme),
    ...(expandedWidth ? { '--rqd-expanded-width': expandedWidth } : {}),
  }
  const posClass = `rqd-${position}`
  const sideClass = `rqd-fab-side-${positionSide(position)}`
  const repositionClass = isRepositioning ? ' rqd-repositioning' : ''
  const containedClass = contained ? ' rqd-contained' : ''
  const brandName = ctx.theme?.brandName
  const brandLogo = ctx.theme?.logo
  const hideBranding = ctx.theme?.hideBranding === true
  const fabLabel = brandName ?? t('widget.title')

  const projectId = ctx.projectId
  const isWorkspaceKey = ctx.apiKey.startsWith('rqd_ws_')
  // Gate all ticket actions behind project selection when:
  //   - authenticated OIDC user with multiple projects (classic multi-project flow), OR
  //   - workspace-scoped API key and no real project resolved yet (never use _current for workspace keys)
  const needsProjectSelection =
    !ctx.isProjectLoading &&
    ctx.projectId === '_current' &&
    ((ctx.isAuthenticated && ctx.projects.length > 1) || isWorkspaceKey)

  function toggleOpen() {
    // Route through the registry so local state and the shared snapshot stay in sync — the
    // registryIsOpen sync effect above flips local `isOpen` once the registry snapshot changes.
    // Flipping local state alone used to revert on the next render because the registry was still
    // closed, keeping the panel hidden.
    registry.toggle()
    if (!isOpen) {
      setView(needsProjectSelection ? 'select-project' : 'home')
      setSelectedTicketId(null)
    }
  }

  function goHome() {
    setView('home')
    setSelectedTicketId(null)
    setEmailCleared(false)
  }

  function handleLangChange(lang: string) {
    setUserLang(lang)
    saveWidgetConfig(ctx.apiKey, { language: lang })
  }

  function handleThemeChange(mode: string) {
    setUserThemeMode(mode)
    saveWidgetConfig(ctx.apiKey, { theme: { ...ctx.theme, mode: mode as 'light' | 'dark' | 'auto', primaryColor: activeColor } })
  }

  function handleColorChange(color: string) {
    setUserColor(color)
    saveWidgetConfig(ctx.apiKey, { theme: { ...ctx.theme, mode: activeThemeMode, primaryColor: color } })
  }

  function handleCornerChange(corner: WidgetPosition) {
    if (corner === position) return
    if (repositionTimer.current) clearTimeout(repositionTimer.current)
    setIsRepositioning(true)
    repositionTimer.current = setTimeout(() => {
      setPosition(corner)
      saveWidgetConfig(ctx.apiKey, { position: corner })
      repositionTimer.current = setTimeout(() => {
        setIsRepositioning(false)
        repositionTimer.current = null
      }, 20)
    }, 120)
  }

  // Display-mode preference handlers. Writes flow through registry.setPreferences, which
  // re-resolves currentDisplayMode, persists to localStorage, and fires onPreferencesChange.
  // The picker reads its active state back from the live currentDisplayMode snapshot so a
  // host-driven setDisplayMode also reflects visually.
  const activeDisplayMode = currentDisplayMode.mode
  const activeSheetSide = currentDisplayMode.side ?? 'end'

  function handleDisplayModeChange(mode: 'popover' | 'side-sheet' | 'bottom-sheet') {
    if (mode === activeDisplayMode) return
    registry.setPreferences({
      display: {
        mode,
        // Preserve the current side selection so switching to side-sheet-then-back keeps the
        // user's earlier start/end choice.
        side: mode === 'side-sheet' ? activeSheetSide : undefined,
      },
    })
  }

  function handleSheetSideChange(side: 'start' | 'end') {
    if (side === activeSheetSide) return
    registry.setPreferences({ display: { mode: activeDisplayMode, side } })
  }

  const COLOR_PRESETS = [
    { color: '#0F5E56', label: 'Green' },
    { color: '#3b82f6', label: 'Blue' },
    { color: '#8b5cf6', label: 'Purple' },
    { color: '#f59e0b', label: 'Orange' },
    { color: '#ef4444', label: 'Red' },
  ]

  function handleClearEmail() {
    clearWidgetEmail(ctx.apiKey)
    setEmailCleared(true)
    setTimeout(() => setEmailCleared(false), 2000)
  }

  function openTicketDetail(ticketId: string) {
    setSelectedTicketId(ticketId)
    setView('ticket-detail')
  }

  const currentProjectName = ctx.projects.find(p => p.id === ctx.projectId)?.name

  const menuItems: Array<{ key: WidgetView; icon: string; label: string; desc: string }> = [
    { key: 'new-ticket', icon: ICONS.newTicket, label: t('menu.newTicket'), desc: t('menu.newTicketDesc') },
    { key: 'my-tickets', icon: ICONS.tickets, label: t('menu.myTickets'), desc: t('menu.myTicketsDesc') },
    { key: 'track', icon: ICONS.track, label: t('menu.trackTicket'), desc: t('menu.trackTicketDesc') },
    // { key: 'kb', icon: ICONS.kb, label: t('menu.knowledgeBase'), desc: t('menu.knowledgeBaseDesc') },
    { key: 'preferences', icon: ICONS.settings, label: t('menu.preferences'), desc: t('menu.preferencesDesc') },
  ]

  // Add "Switch project" option when OIDC user has multiple projects
  if (ctx.isMultiProject) {
    menuItems.splice(-1, 0, {
      key: 'select-project',
      icon: ICONS.switchProject,
      label: t('project.switch'),
      desc: currentProjectName ?? t('project.switchDesc'),
    })
  }

  function renderProjectPicker() {
    if (ctx.isProjectLoading) {
      return <div className="rqd-loading">{t('project.loading')}</div>
    }
    // Workspace key + not logged in → prompt to log in first
    if (isWorkspaceKey && !ctx.isAuthenticated) {
      return (
        <div className="rqd-placeholder">
          <p>{t('auth.loginRequired')}</p>
          <button className="rqd-primary-btn" onClick={() => login()}>
            {t('auth.login')}
          </button>
        </div>
      )
    }
    if (ctx.projects.length === 0) {
      return <div className="rqd-placeholder"><p>{t('project.noProjects')}</p></div>
    }
    return (
      <div className="rqd-menu">
        <p style={{ padding: '0 16px 8px', margin: 0, fontSize: 13, opacity: 0.7 }}>{t('project.select')}</p>
        {ctx.projects.map(project => (
          <button
            key={project.id}
            className={`rqd-menu-item${project.id === ctx.projectId ? ' rqd-active' : ''}`}
            onClick={() => {
              ctx.selectProject(project.id)
              setView('home')
            }}
          >
            <div className="rqd-menu-icon">
              <SvgIcon path={ICONS.switchProject} size={22} />
            </div>
            <div className="rqd-menu-text">
              <span className="rqd-menu-label">{project.name}</span>
              <span className="rqd-menu-desc">{project.slug}</span>
            </div>
          </button>
        ))}
      </div>
    )
  }

  function renderViewContent() {
    // Force project picker if OIDC user hasn't selected a project yet
    if (needsProjectSelection && view !== 'select-project') {
      return renderProjectPicker()
    }

    switch (view) {
      case 'home': {
        // Built-in items are rendered through MenuList so that custom actions registered via
        // the v1.2 registry interleave correctly via anchor.before / anchor.after.
        const builtIns: BuiltInMenuItem[] = menuItems.map(item => ({
          id: item.key,
          iconPath: item.icon,
          label: item.label,
          description: item.desc,
          onActivate: () => setView(item.key),
        }))
        return (
          <>
            {ctx.isMultiProject && currentProjectName && (
              <div className="rqd-project-badge">
                <SvgIcon path={ICONS.switchProject} size={14} />
                <span>{currentProjectName}</span>
              </div>
            )}
            <MenuList builtIns={builtIns} />
          </>
        )
      }

      case 'select-project':
        return renderProjectPicker()

      case 'new-ticket':
        return (
          <SubmitTicketView
            projectId={projectId}
            onSuccess={(result) => {
              onTicketCreated?.(result)
            }}
            onError={onError}
            isAuthenticated={ctx.isAuthenticated}
            userEmail={ctx.userEmail}
          />
        )

      case 'my-tickets':
        return (
          <MyTicketsView
            projectId={projectId}
            onSelectTicket={openTicketDetail}
            isAuthenticated={ctx.isAuthenticated}
            userEmail={ctx.userEmail}
          />
        )

      case 'ticket-detail':
        if (!selectedTicketId) return null
        return (
          <TicketDetailView
            ticketId={selectedTicketId}
            onBack={() => setView('my-tickets')}
          />
        )

      case 'track':
        return (
          <TrackTicketView
            onTrackSuccess={openTicketDetail}
          />
        )

      case 'kb':
        return (
          <div className="rqd-placeholder">
            <SvgIcon path={ICONS.kb} size={40} />
            <p>{t('menu.kbPlaceholder')}</p>
          </div>
        )

      case 'preferences':
        return (
          <div className="rqd-prefs">
            <div className="rqd-prefs-group">
              <span className="rqd-prefs-label">{t('prefs.language')}</span>
              <div className="rqd-prefs-options">
                <button
                  className={`rqd-prefs-option${activeLang === 'en' ? ' rqd-active' : ''}`}
                  onClick={() => handleLangChange('en')}
                >
                  English
                </button>
                <button
                  className={`rqd-prefs-option${activeLang === 'ar' ? ' rqd-active' : ''}`}
                  onClick={() => handleLangChange('ar')}
                >
                  العربية
                </button>
              </div>
            </div>

            <div className="rqd-prefs-group">
              <span className="rqd-prefs-label">{t('prefs.theme')}</span>
              <div className="rqd-prefs-options">
                {(['light', 'dark', 'auto'] as const).map(mode => (
                  <button
                    key={mode}
                    className={`rqd-prefs-option${activeThemeMode === mode ? ' rqd-active' : ''}`}
                    onClick={() => handleThemeChange(mode)}
                  >
                    {t(`prefs.${mode}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Accent Color */}
            <div className="rqd-prefs-group">
              <span className="rqd-prefs-label">{t('prefs.accentColor')}</span>
              <div className="rqd-color-presets">
                {COLOR_PRESETS.map(preset => (
                  <button
                    key={preset.color}
                    className={`rqd-color-preset${activeColor.toLowerCase() === preset.color.toLowerCase() ? ' rqd-active' : ''}`}
                    style={{ background: preset.color, color: preset.color }}
                    onClick={() => handleColorChange(preset.color)}
                    aria-label={preset.label}
                    title={preset.label}
                  />
                ))}
              </div>
            </div>

            {/* Widget position */}
            <div className="rqd-prefs-group">
              <span className="rqd-prefs-label">{t('prefs.position')}</span>
              <div className="rqd-corner-picker">
                {CORNERS.map(corner => {
                  const label = t(CORNER_LABEL_KEY[corner])
                  return (
                    <button
                      key={corner}
                      type="button"
                      className={`rqd-corner-tile rqd-${corner}${position === corner ? ' rqd-active' : ''}`}
                      onClick={() => handleCornerChange(corner)}
                      aria-label={label}
                      title={label}
                      aria-pressed={position === corner}
                    />
                  )
                })}
              </div>
            </div>

            {/* Display mode — kiosk-style hosts can hide this via ReqdeskProvider.hideDisplayModePicker */}
            {!ctx.hideDisplayModePicker && (
              <div className="rqd-prefs-group">
                <span className="rqd-prefs-label">{t('prefs.displayMode')}</span>
                <div className="rqd-prefs-options">
                  {(['popover', 'side-sheet', 'bottom-sheet'] as const).map(mode => (
                    <button
                      key={mode}
                      type="button"
                      className={`rqd-prefs-option${activeDisplayMode === mode ? ' rqd-active' : ''}`}
                      onClick={() => handleDisplayModeChange(mode)}
                      aria-pressed={activeDisplayMode === mode}
                    >
                      {t(`prefs.display.${mode}`)}
                    </button>
                  ))}
                </div>
                {activeDisplayMode === 'side-sheet' && (
                  <>
                    <span className="rqd-prefs-label" style={{ marginTop: 8, fontSize: 12 }}>
                      {t('prefs.sheetSide')}
                    </span>
                    <div className="rqd-prefs-options">
                      {(['start', 'end'] as const).map(side => (
                        <button
                          key={side}
                          type="button"
                          className={`rqd-prefs-option${activeSheetSide === side ? ' rqd-active' : ''}`}
                          onClick={() => handleSheetSideChange(side)}
                          aria-pressed={activeSheetSide === side}
                        >
                          {t(`prefs.side.${side}`)}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Clear saved email */}
            {loadWidgetEmail(ctx.apiKey) && (
              <div className="rqd-prefs-group">
                <button
                  className="rqd-btn rqd-btn-secondary"
                  onClick={handleClearEmail}
                  style={{ width: 'auto', padding: '8px 16px' }}
                >
                  {emailCleared ? t('prefs.emailCleared') : t('prefs.clearEmail')}
                </button>
              </div>
            )}

            {/* Reset to host defaults — only shown when the host supplied initialPreferences,
                per US5 acceptance ("reset to host defaults" control appears only when defaults
                exist). */}
            {registry.hasInitialPreferences() && (
              <div className="rqd-prefs-group">
                <button
                  className="rqd-btn rqd-btn-secondary"
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.confirm?.(t('prefs.resetConfirm')) === false) return
                    registry.resetToInitialPreferences()
                    const next = registry.getPreferences()
                    if (next.language) setUserLang(next.language)
                    if (next.themeMode) setUserThemeMode(next.themeMode)
                    if (next.primaryColor) setUserColor(next.primaryColor)
                    if (next.position) setPosition(next.position)
                  }}
                  style={{ width: 'auto', padding: '8px 16px' }}
                >
                  {t('prefs.resetToDefaults')}
                </button>
              </div>
            )}
          </div>
        )
    }
  }

  const viewTitle: Record<WidgetView, string> = {
    home: brandName ?? t('widget.title'),
    'select-project': t('project.select'),
    'new-ticket': t('widget.newTicket'),
    'my-tickets': t('menu.myTickets'),
    'ticket-detail': t('menu.myTickets'),
    track: t('widget.trackTicket'),
    kb: t('menu.knowledgeBase'),
    preferences: t('prefs.title'),
  }

  const canGoBack = view !== 'home'
  const goBackTarget = view === 'ticket-detail' ? 'my-tickets' : 'home'

  return (
    <ShadowRoot>
      <div
        style={cssVars as React.CSSProperties}
        {...(isRtl ? { dir: 'rtl' } : {})}
      >
        {!hideFab && (
          <button
            className={`rqd-fab ${posClass}${containedClass}${!isOpen ? ` rqd-fab-labeled ${sideClass}` : ''}${repositionClass}`}
            style={cssVars as React.CSSProperties}
            onClick={toggleOpen}
            aria-label={isOpen ? t('widget.close') : fabLabel}
          >
            {isOpen
              ? <SvgIcon path={ICONS.close} size={24} />
              : renderFabIcon(ctx.fabIcon, 24)}
            {!isOpen && <span className="rqd-fab-label" aria-hidden="true">{fabLabel}</span>}
          </button>
        )}

        {isOpen && isExpanded && (
          <div
            className="rqd-backdrop"
            onClick={() => setIsExpanded(false)}
            aria-hidden="true"
          />
        )}

        {/* Sheet-mode backdrop — desktop-only (≥640px) per design. Clicks close unless
            dismissOnBackdrop is explicitly false. Popover mode has no backdrop. */}
        {isOpen && currentDisplayMode.mode !== 'popover' && (
          <div
            className="rqd-backdrop-sheet"
            aria-hidden="true"
            onClick={() => {
              if (currentDisplayMode.dismissOnBackdrop !== false) {
                setIsOpen(false)
                registry.close()
              }
            }}
          />
        )}

        <div
          className={buildPanelClassName({
            mode: currentDisplayMode.mode,
            side: currentDisplayMode.side,
            posClass,
            contained,
            isOpen,
            isExpanded,
            isRepositioning,
          })}
          style={buildPanelStyle(cssVars, currentDisplayMode)}
        >
          <div className="rqd-header">
            <div className="rqd-header-brand">
              {canGoBack && (
                <button
                  className="rqd-header-close"
                  onClick={() => goBackTarget === 'home' ? goHome() : setView(goBackTarget as WidgetView)}
                  aria-label={t('tracker.back')}
                >
                  <SvgIcon path={isRtl ? ICONS.backRtl : ICONS.back} size={18} />
                </button>
              )}
              {view === 'home' && brandLogo && (
                <img src={brandLogo} alt="" className="rqd-header-logo" />
              )}
              <span className="rqd-header-title">{viewTitle[view]}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {(ctx.auth || isAuthConfigured()) && view === 'home' && ctx.serverAllowedModes.includes('sso') && (
                ctx.isAuthenticated ? (
                  <div className="rqd-auth-user" title={ctx.userEmail ?? ctx.userName}>
                    <span className="rqd-auth-user-name">{ctx.userName ?? ctx.userEmail}</span>
                    <button
                      className="rqd-header-close"
                      onClick={() => logout()}
                      aria-label={t('auth.logout')}
                      title={t('auth.logout')}
                    >
                      <SvgIcon path={ICONS.logout} size={16} />
                    </button>
                  </div>
                ) : (
                  // Hide the SSO login button when signed mode has already produced a verified
                  // identity — the widget goes straight to the form.
                  ctx.resolvedIdentityMode.kind !== 'signed' && ctx.resolvedIdentityMode.kind !== 'unsigned' && (
                    <button
                      className="rqd-auth-btn"
                      onClick={() => login()}
                      aria-label={t('auth.login')}
                    >
                      {t('auth.login')}
                    </button>
                  )
                )
              )}
              <button
                className="rqd-header-close"
                onClick={() => setIsExpanded(v => !v)}
                aria-label={t(isExpanded ? 'widget.collapse' : 'widget.expand')}
                title={t(isExpanded ? 'widget.collapse' : 'widget.expand')}
              >
                <SvgIcon path={isExpanded ? ICONS.collapse : ICONS.expand} size={18} />
              </button>
              <button
                className="rqd-header-close"
                onClick={toggleOpen}
                aria-label={t('widget.close')}
              >
                <SvgIcon path={ICONS.close} size={18} />
              </button>
            </div>
          </div>

          <div className="rqd-body">
            <NotificationStack dismissLabel={t('notification.dismiss')} />
            {renderViewContent()}
          </div>

          {!hideBranding && (
            <div className="rqd-footer">
              <span>{t('branding.poweredBy')}</span>
              <a href="https://reqdesk.com" target="_blank" rel="noopener noreferrer">Reqdesk</a>
            </div>
          )}
        </div>
      </div>
    </ShadowRoot>
  )
}
